import React from 'react';

import { I18nText } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import { ThreeDStockContentSearchInImageView } from '../ThreeDStockContentSearchInImage/ThreeDStockContentSearchInImageTypes';

import {
  ThreeDStockContentSortDropdownButton,
  ThreeDStockContentSortDropdownButtonButtonsType
} from './components/ThreeDStockContentSortDropdownButton';
import { ThreeDStockContentImageScaleButton } from './components/ThreeDStockContentImageScaleButton';
import { ThreeDStockContentImageToggleViewButton } from './components/ThreeDStockContentImageToggleViewButton';
import { ThreeDStockContentEditProductsButton } from './components/ThreeDStockContentEditProductsButton';
import { ThreeDStockContentEditLifestylesButton } from './components/ThreeDStockContentEditLifestylesButton';
import { ThreeDStockContentEditMaterialsButton } from './components/ThreeDStockContentEditMaterialsButton';
import { ThreeDStockContentCompanyLibraryEditProductsButton } from './components/ThreeDStockContentCompanyLibraryEditProductsButton';
import { ThreeDStockContentCompanyLibraryEditLifestylesButton } from './components/ThreeDStockContentCompanyLibraryEditLifestylesButton';
import { ThreeDStockContentCompanyLibraryEditMaterialsButton } from './components/ThreeDStockContentCompanyLibraryEditMaterialsButton';
import { ThreeDStockContentPageInfo } from './components/ThreeDStockContentPageInfo';
import { ThreeDStockContentSearchInImageViewSwitch } from './components/ThreeDStockContentSearchInImageViewSwitch';

import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';

import { StockTabs } from '../../../stockConstants';

interface ThreeDStockContentSortAndViewProps {
  tab: StockTabs;
  sortButtons: ThreeDStockContentSortDropdownButtonButtonsType[];
  currentSortI18nText: I18nText;
  totalCount?: number;
  page?: number;
  limit?: number;
  searchText?: string;
  withOpenFiltersButton?: boolean;
  withSelectedFilters?: boolean;
  openFilters?: () => void;
  withTotal?: boolean;
  withImageScaleButton?: boolean;
  withImageToggleViewButton?: boolean;
  withEditProductsButton?: boolean;
  withEditLifestylesButton?: boolean;
  withEditMaterialsButton?: boolean;
  withCompanyLibraryEditProductsButton?: boolean;
  withCompanyLibraryEditLifestylesButton?: boolean;
  withCompanyLibraryEditMaterialsButton?: boolean;
  companyNanoId?: TeamNanoID;
  isMyLibrary?: boolean;
  searchInImageView?: ThreeDStockContentSearchInImageView;
  detectedObjectsCount?: number;
  onSearchInImageViewChange?: (
    view: ThreeDStockContentSearchInImageView
  ) => void;
}

function ThreeDStockContentSortAndView({
  tab,
  sortButtons,
  currentSortI18nText,
  totalCount,
  page,
  limit,
  searchText,
  openFilters,
  withOpenFiltersButton,
  withSelectedFilters,
  withTotal,
  withImageScaleButton,
  withImageToggleViewButton,
  withEditProductsButton,
  withEditLifestylesButton,
  withEditMaterialsButton,
  withCompanyLibraryEditProductsButton,
  withCompanyLibraryEditLifestylesButton,
  withCompanyLibraryEditMaterialsButton,
  companyNanoId,
  isMyLibrary,
  searchInImageView,
  detectedObjectsCount,
  onSearchInImageViewChange
}: ThreeDStockContentSortAndViewProps) {
  return (
    <div className="w-full flex justify-between items-start gap-2 flex-wrap xl:flex-nowrap min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        {withOpenFiltersButton && (
          <div className="relative inline-flex items-center justify-center shrink-0">
            <PureIconButtonHelper
              className="py-2 pl-2 pr-2 rounded-md inline-flex items-center leading-6 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
              onClick={openFilters}
              icon={IconsEnum.FILTER_SOLID}
              iconClassName="h-6 w-6 p-0.5"
              dataGa="close-filters"
            />

            {withSelectedFilters && (
              <span className="absolute rounded-full text-xs text-center font-semibold leading-4 w-3 h-3 px-0.5 bg-red-500 text-white top-0 right-0 translate-x-1/3 -translate-y-1/3" />
            )}
          </div>
        )}

        {searchInImageView && onSearchInImageViewChange ? (
          <ThreeDStockContentSearchInImageViewSwitch
            view={searchInImageView}
            onChange={onSearchInImageViewChange}
            disabled={!detectedObjectsCount}
          />
        ) : (
          <ThreeDStockContentSortDropdownButton
            buttons={sortButtons}
            currentSortI18nText={currentSortI18nText}
          />
        )}
      </div>

      {withTotal && (
        <ThreeDStockContentPageInfo
          totalCount={totalCount}
          page={page}
          limit={limit}
          searchText={searchText}
          searchInImageView={searchInImageView}
          detectedObjectsCount={detectedObjectsCount}
        />
      )}

      <div className="flex items-center gap-2">
        {withImageScaleButton && (
          <ThreeDStockContentImageScaleButton tab={tab} />
        )}

        {withImageToggleViewButton && searchInImageView !== 'all' && (
          <ThreeDStockContentImageToggleViewButton tab={tab} />
        )}

        {withEditProductsButton && searchInImageView !== 'all' && (
          <ThreeDStockContentEditProductsButton />
        )}

        {withEditLifestylesButton && <ThreeDStockContentEditLifestylesButton />}

        {withEditMaterialsButton && <ThreeDStockContentEditMaterialsButton />}

        {withCompanyLibraryEditProductsButton && (
          <ThreeDStockContentCompanyLibraryEditProductsButton
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
          />
        )}

        {withCompanyLibraryEditLifestylesButton && (
          <ThreeDStockContentCompanyLibraryEditLifestylesButton
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
          />
        )}

        {withCompanyLibraryEditMaterialsButton && (
          <ThreeDStockContentCompanyLibraryEditMaterialsButton
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
          />
        )}
      </div>
    </div>
  );
}

export default ThreeDStockContentSortAndView;
